import {
  RC_CLIENT_ID,
  RC_REDIRECT_URI,
  RC_SERVER_URL,
  STORAGE_KEYS,
} from './config';

const b64UrlEncode = (arrayBuffer) => {
  let str = String.fromCharCode.apply(null, new Uint8Array(arrayBuffer));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomString = (length = 64) => {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  for (let i = 0; i < length; i += 1) {
    result += charset[values[i] % charset.length];
  }
  return result;
};

const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  return hash;
};

export const rememberPendingNumber = (num) => {
  localStorage.setItem(STORAGE_KEYS.pendingNumber, num);
};

export const consumePendingNumber = () => {
  const v = localStorage.getItem(STORAGE_KEYS.pendingNumber);
  if (v) localStorage.removeItem(STORAGE_KEYS.pendingNumber);
  return v;
};

export const getReturnPath = () => {
  const p =
    localStorage.getItem(STORAGE_KEYS.returnPath) ||
    sessionStorage.getItem(STORAGE_KEYS.returnPath);
  return p;
};

export const clearReturnPath = () => {
  localStorage.removeItem(STORAGE_KEYS.returnPath);
  sessionStorage.removeItem(STORAGE_KEYS.returnPath);
};

export const getStoredTokens = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.tokens);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setStoredTokens = (tokens) => {
  localStorage.setItem(STORAGE_KEYS.tokens, JSON.stringify(tokens));
};

export const getAccessToken = () => {
  const tokens = getStoredTokens();
  return tokens?.access_token || null;
};

export const isTokenExpired = () => {
  const tokens = getStoredTokens();
  if (!tokens) return true;
  if (!tokens.obtained_at || !tokens.expires_in) return true;
  const expiresAt = tokens.obtained_at + tokens.expires_in * 1000 - 60000; // 60s early
  return Date.now() >= expiresAt;
};

export const ensureAuthorized = async () => {
  const tokens = getStoredTokens();
  if (!tokens) return false;
  if (!isTokenExpired()) return true;
  // try refresh
  if (!tokens.refresh_token) return false;
  try {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: tokens.refresh_token,
      client_id: RC_CLIENT_ID,
    });
    const res = await fetch(`${RC_SERVER_URL}/restapi/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) throw new Error('Token refresh failed');
    const data = await res.json();
    setStoredTokens({ ...data, obtained_at: Date.now() });
    return true;
  } catch (e) {
    return false;
  }
};

// True when the browser can support calling at all: PKCE hashing (WebCrypto)
// and the microphone (getUserMedia) both exist only in secure contexts —
// HTTPS or localhost. Plain http on a LAN IP (phone-testing the dev server)
// has neither.
export const isCallingSupported = () =>
  !!window.crypto?.subtle && !!navigator.mediaDevices?.getUserMedia;

const buildAuthorizeUrl = async () => {
  if (!isCallingSupported()) {
    throw new Error(
      'Calling needs a secure (HTTPS) connection. Open the app over https, or use it from this machine via localhost.',
    );
  }
  const verifier = generateRandomString(64);
  const challenge = await sha256(verifier).then(b64UrlEncode);
  localStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);
  sessionStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);

  const authUrl = new URL(`${RC_SERVER_URL}/restapi/oauth/authorize`);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('client_id', RC_CLIENT_ID);
  authUrl.searchParams.set('redirect_uri', RC_REDIRECT_URI);
  authUrl.searchParams.set('code_challenge', challenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  authUrl.searchParams.set('prompt', 'login consent');
  return authUrl.toString();
};

export const redirectToAuthorize = async () => {
  const path = `${window.location.pathname}${window.location.search}`;
  localStorage.setItem(STORAGE_KEYS.returnPath, path);
  sessionStorage.setItem(STORAGE_KEYS.returnPath, path);
  const url = await buildAuthorizeUrl();
  window.location.assign(url);
};

/**
 * RC sign-in in a POPUP so the opener never navigates (drafts survive).
 * localStorage is shared per-origin, so the popup's callback stores the
 * tokens where the opener reads them. Resolves true once tokens exist,
 * false when the popup is blocked, closed without finishing, or times out.
 */
export const openAuthorizePopup = async () => {
  const url = await buildAuthorizeUrl();
  localStorage.setItem(STORAGE_KEYS.popupLogin, '1');
  const popup = window.open(
    url,
    'rc-login',
    'width=500,height=720,menubar=no,toolbar=no',
  );
  if (!popup) {
    localStorage.removeItem(STORAGE_KEYS.popupLogin);
    return false; // popup blocked — caller falls back to its toast
  }
  const startedAt = Date.now();
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      const tokens = getStoredTokens();
      const fresh = tokens?.obtained_at && tokens.obtained_at >= startedAt;
      if (fresh) {
        clearInterval(timer);
        localStorage.removeItem(STORAGE_KEYS.popupLogin);
        try {
          if (!popup.closed) popup.close();
        } catch {
          // cross-origin while on RC's page — it closes itself post-callback
        }
        resolve(true);
        return;
      }
      const gaveUp = popup.closed || Date.now() - startedAt > 3 * 60 * 1000;
      if (gaveUp) {
        clearInterval(timer);
        localStorage.removeItem(STORAGE_KEYS.popupLogin);
        resolve(false);
      }
    }, 500);
  });
};

export const isPopupLogin = () =>
  localStorage.getItem(STORAGE_KEYS.popupLogin) === '1';

export const handleCallback = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) throw new Error('Missing authorization code');
  const verifier =
    localStorage.getItem(STORAGE_KEYS.pkceVerifier) ||
    sessionStorage.getItem(STORAGE_KEYS.pkceVerifier);
  if (!verifier) throw new Error('Missing PKCE verifier');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: RC_REDIRECT_URI,
    client_id: RC_CLIENT_ID,
    code_verifier: verifier,
  });

  const res = await fetch(`${RC_SERVER_URL}/restapi/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${text}`);
  }
  const tokens = await res.json();
  setStoredTokens({ ...tokens, obtained_at: Date.now() });
  localStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  return tokens;
};

export const signOutRc = () => {
  localStorage.removeItem(STORAGE_KEYS.tokens);
  localStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  localStorage.removeItem(STORAGE_KEYS.pendingNumber);
  localStorage.removeItem(STORAGE_KEYS.returnPath);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.returnPath);
};
